import axios from 'axios'
import { toastr } from 'react-redux-toastr'
import { initialize } from 'redux-form'

const LIST_URL = `${process.env.REACT_APP_API_HOST}/bot_orchestrators?orderBy=name&sortedBy=asc&limit=200`

const INITIAL_STATE = {
    list: [],
    botUsers: [],
    show: 'list',
    errors: {}
}

const INITIAL_VALUES = {
    name: '',
    description: '',
    user_ids: [],
    enabled: 1,
    dry_run: 1,
}

// Colunas graváveis. Atributos calculados (online, users, user_label) e os
// escritos pela máquina no heartbeat fazem a ACL recusar o POST/PUT.
const WRITABLE_FIELDS = ['name', 'description', 'user_ids', 'enabled', 'dry_run']

function pickWritable(values, extra = {}) {
    const payload = {}
    WRITABLE_FIELDS.forEach((field) => {
        if (values[field] !== undefined) payload[field] = values[field]
    })

    let userIds = payload.user_ids
    if (userIds == null && values.user_id != null && values.user_id !== '') {
        userIds = [values.user_id]
    }
    if (!Array.isArray(userIds)) {
        userIds = userIds === '' || userIds == null ? [] : [userIds]
    }
    payload.user_ids = userIds
        .filter((id) => id !== '' && id != null)
        .map((id) => Number(id))

    payload.enabled = Number(payload.enabled) ? 1 : 0
    payload.dry_run = Number(payload.dry_run) ? 1 : 0
    return { ...payload, ...extra }
}

export function getList() {
    return dispatch => {
        axios.get(LIST_URL, { silent: true })
            .then(resp => {
                dispatch({ type: 'BOT_ORCHESTRATOR_FETCHED', payload: resp })
            })
            .catch(e => {
                const msg = (e.response && e.response.data && e.response.data.message)
                    || 'Falha ao carregar orquestradores'
                toastr.error('Erro', msg)
                dispatch({ type: 'BOT_ORCHESTRATOR_FETCHED', payload: { data: { data: [] } } })
            })
    }
}

/**
 * Usuários do perfil Bot. A filtragem de "já ativos em outro orquestrador"
 * acontece na UI com a lista de máquinas — o admin só vê livres + os da linha.
 */
export function getBotUsers() {
    return dispatch => {
        axios.get(`${process.env.REACT_APP_API_HOST}/profiles?limit=100`, { silent: true })
            .then(resp => {
                const profiles = (resp.data && resp.data.data) || []
                const bot = profiles.find((p) => p.noun === 'Bot')
                if (!bot) {
                    toastr.warning('Ops!', 'Perfil Bot não encontrado. Rode o database/crash.sql.')
                    return
                }
                return axios.get(
                    `${process.env.REACT_APP_API_HOST}/user_profiles?search=profile_id:${bot.id}&with=user:id,name,login&limit=200`,
                    { silent: true }
                ).then(userResp => {
                    const rows = (userResp.data && userResp.data.data) || []
                    const botUsers = rows
                        .filter((row) => row.user && row.user.id)
                        .map((row) => ({ id: row.user.id, name: row.user.name || row.user.login }))
                    dispatch({ type: 'BOT_ORCHESTRATOR_USERS_FETCHED', payload: botUsers })
                })
            })
            .catch(e => {
                const msg = (e.response && e.response.data && e.response.data.message)
                    || 'Falha ao carregar usuários do perfil Bot'
                toastr.error('Erro', msg)
            })
    }
}

export function create(values) {
    return submit(values, 'post')
}

export function update(values) {
    return submit(values, 'put')
}

export function remove(values) {
    return dispatch => {
        if (!window.confirm(`Remover o orquestrador "${values.name}"?`)) return

        axios.delete(`${process.env.REACT_APP_API_HOST}/bot_orchestrators/${values.id}`)
            .then(() => {
                toastr.success('Sucesso', 'Orquestrador removido.')
                dispatch(init())
                dispatch(getList())
            })
            .catch(e => reportError(e))
    }
}

export function toggleEnabled(orchestrator) {
    return patch(orchestrator, { enabled: Number(orchestrator.enabled) === 1 ? 0 : 1 })
}

export function toggleDryRun(orchestrator) {
    return patch(orchestrator, { dry_run: Number(orchestrator.dry_run) === 1 ? 0 : 1 })
}

/** Substitui a lista de bots desta máquina (vários por orquestrador). */
export function assignUsers(orchestrator, userIds) {
    const ids = Array.isArray(userIds)
        ? userIds.map((id) => Number(id)).filter((id) => Number.isFinite(id))
        : []
    return patch(orchestrator, { user_ids: ids })
}

/** @deprecated use assignUsers — mantido para compat com selects antigos. */
export function assignUser(orchestrator, userId) {
    const current = Array.isArray(orchestrator.user_ids) ? [...orchestrator.user_ids] : []
    const id = userId === '' || userId == null ? null : Number(userId)
    if (id == null) return assignUsers(orchestrator, [])
    if (current.indexOf(id) === -1) current.push(id)
    return assignUsers(orchestrator, current)
}

export function rotateKey(orchestrator) {
    return dispatch => {
        axios.put(
            `${process.env.REACT_APP_API_HOST}/bot_orchestrators/${orchestrator.id}`,
            pickWritable(orchestrator, { access_key: '' })
        )
            .then(() => {
                toastr.success('Sucesso', 'Nova chave gerada. Atualize a máquina.')
                dispatch(getList())
            })
            .catch(e => reportError(e))
    }
}

function patch(orchestrator, changes) {
    return dispatch => {
        axios.put(
            `${process.env.REACT_APP_API_HOST}/bot_orchestrators/${orchestrator.id}`,
            pickWritable({ ...orchestrator, ...changes })
        )
            .then(() => {
                dispatch(getList())
            })
            .catch(e => reportError(e))
    }
}

function submit(values, method) {
    return dispatch => {
        const id = values.id ? values.id + 0 : ''

        dispatch(getValidation({}))
        axios[method](
            `${process.env.REACT_APP_API_HOST}/bot_orchestrators${id ? '/' + id : ''}`,
            pickWritable(values)
        )
            .then(resp => {
                if (method === 'post') {
                    const key = resp.data && resp.data.data && resp.data.data.access_key
                    if (key) {
                        toastr.success('Orquestrador criado', 'Copie a chave de acesso na edição da máquina.')
                    }
                }
                dispatch(init())
                dispatch(getList())
            })
            .catch(e => {
                if (e.response && e.response.data && e.response.data.errors) {
                    dispatch(getValidation(e.response.data.errors))
                } else {
                    reportError(e)
                }
            })
    }
}

function reportError(e) {
    if (!e.response) {
        toastr.error('Erro', 'Desconhecido :-/')
        console.log(e)
    } else if (e.response.data && e.response.data.message) {
        toastr.error('Erro', e.response.data.message)
    } else {
        toastr.error('Erro', 'Falha na operação')
    }
}

export function showContent(flag) {
    return { type: 'BOT_ORCHESTRATOR_CONTENT_CHANGED', payload: flag }
}

export function showUpdate(orchestrator) {
    return [
        showContent('form'),
        initialize('botOrchestratorForm', {
            id: orchestrator.id,
            name: orchestrator.name,
            description: orchestrator.description || '',
            user_ids: Array.isArray(orchestrator.user_ids) ? orchestrator.user_ids : [],
            enabled: Number(orchestrator.enabled) ? 1 : 0,
            dry_run: Number(orchestrator.dry_run) ? 1 : 0,
            access_key: orchestrator.access_key,
        })
    ]
}

export function showCreate() {
    return [
        showContent('form'),
        initialize('botOrchestratorForm', INITIAL_VALUES)
    ]
}

export function init() {
    return [
        initialize('botOrchestratorForm', INITIAL_VALUES),
        showContent('list')
    ]
}

export function getValidation(errors) {
    return { type: 'BOT_ORCHESTRATOR_FORM_ERRORS', payload: errors }
}

export const botOrchestratorReducer = (state = INITIAL_STATE, action) => {
    switch (action.type) {
        case 'BOT_ORCHESTRATOR_FETCHED':
            return { ...state, list: action.payload.data ? action.payload.data.data : [] }
        case 'BOT_ORCHESTRATOR_USERS_FETCHED':
            return { ...state, botUsers: action.payload }
        case 'BOT_ORCHESTRATOR_CONTENT_CHANGED':
            return { ...state, show: action.payload }
        case 'BOT_ORCHESTRATOR_FORM_ERRORS':
            return { ...state, errors: action.payload }
        default:
            return state
    }
}
